import { useEffect, useMemo, useState } from 'react';
import { Calendar, Check, Mail, MapPin, ShieldCheck, Users } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { PendingTournamentInvitation } from '@golf-app/shared';

import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';

function safeNextPath(value: string | null): string {
  if (!value || !value.startsWith('/app/') || value.startsWith('/app/invitations')) {
    return '/app/home';
  }
  return value;
}

function invitationRoleLabel(role: PendingTournamentInvitation['roleType']): string {
  if (role === 'captain') return 'Team captain';
  if (role === 'scorekeeper') return 'Scorekeeper';
  return 'Player';
}

function formatEventDate(value: string | null): string | null {
  if (!value) return null;
  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function PendingTournamentInvitations() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = useMemo(
    () => safeNextPath(searchParams.get('next')),
    [searchParams],
  );
  const [invitations, setInvitations] = useState<PendingTournamentInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [deliveryMessage, setDeliveryMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    api.getPendingTournamentInvitations()
      .then((result) => {
        if (active) setInvitations(result.invitations);
      })
      .catch((requestError: unknown) => {
        if (active) {
          setError(requestError instanceof Error
            ? requestError.message
            : 'Invitations could not be checked.');
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!loading && !error && invitations.length === 0) {
      navigate(nextPath, { replace: true });
    }
  }, [error, invitations.length, loading, navigate, nextPath]);

  async function requestCode(invitation: PendingTournamentInvitation) {
    setBusy(true);
    setError(null);
    setCode('');
    try {
      const result = await api.requestTournamentInvitationCode(invitation.invitationId);
      setSelectedId(invitation.invitationId);
      setDeliveryMessage(`We sent a 6-digit code to ${result.maskedEmail}.`);
    } catch (requestError: unknown) {
      setError(requestError instanceof Error
        ? requestError.message
        : 'The confirmation code could not be sent.');
    } finally {
      setBusy(false);
    }
  }

  async function acceptInvitation(invitation: PendingTournamentInvitation) {
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const player = await api.acceptTournamentInvitation(invitation.invitationId, code);
      navigate(`/app/tournaments/${player.eventId}`, { replace: true });
    } catch (requestError: unknown) {
      setError(requestError instanceof Error
        ? requestError.message
        : 'The invitation could not be linked.');
    } finally {
      setBusy(false);
    }
  }

  if (loading || (!error && invitations.length === 0)) {
    return (
      <AppShell>
        <div className="t-loading" aria-label="Checking event invitations">
          <div className="spinner" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <section className="t-hero invitation-hero">
        <div className="invitation-hero-icon"><Mail /></div>
        <div>
          <h1>Event invitation found</h1>
          <p>Confirm your email to connect your account to the roster spot prepared for you.</p>
        </div>
      </section>

      {error && <div className="t-error" role="alert">{error}</div>}

      <div className="invitation-list">
        {invitations.map((invitation) => {
          const isSelected = selectedId === invitation.invitationId;
          const date = formatEventDate(invitation.startDate);
          return (
            <article className="invitation-card" key={invitation.invitationId}>
              <div className="invitation-card-head">
                <div>
                  <div className="invitation-kicker">YOU WERE INVITED AS</div>
                  <h2>{invitation.displayName}</h2>
                  <p>
                    {invitationRoleLabel(invitation.roleType)}
                    {invitation.teamName ? ` · ${invitation.teamName}` : ''}
                    {invitation.eventHandicapIndex != null
                      ? ` · HCP ${invitation.eventHandicapIndex}`
                      : ''}
                  </p>
                </div>
                <ShieldCheck />
              </div>

              <div className="invitation-event-name">{invitation.eventName}</div>
              <div className="invitation-meta">
                {invitation.locationName && <span><MapPin />{invitation.locationName}</span>}
                {date && <span><Calendar />{date}</span>}
                <span><Users />{invitation.matchCount} {invitation.matchCount === 1 ? 'match' : 'matches'}</span>
              </div>

              {!isSelected ? (
                <button
                  type="button"
                  className="t-btn t-btn-primary t-btn-block"
                  disabled={busy}
                  onClick={() => requestCode(invitation)}
                >
                  <Mail /> {busy ? 'Sending…' : 'Send confirmation code'}
                </button>
              ) : (
                <div className="invitation-code-panel">
                  <p>{deliveryMessage}</p>
                  <label className="invitation-code-label">
                    <span>CONFIRMATION CODE</span>
                    <input
                      className="t-input invitation-code-input"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={code}
                      onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      disabled={busy}
                      autoFocus
                    />
                  </label>
                  <button
                    type="button"
                    className="t-btn t-btn-primary t-btn-block"
                    disabled={busy || code.length !== 6}
                    onClick={() => acceptInvitation(invitation)}
                  >
                    <Check /> {busy ? 'Linking…' : 'Confirm and link my account'}
                  </button>
                  <button
                    type="button"
                    className="t-btn t-btn-ghost t-btn-block"
                    disabled={busy}
                    onClick={() => requestCode(invitation)}
                  >
                    Send a new code
                  </button>
                </div>
              )}
            </article>
          );
        })}
      </div>

      <button
        type="button"
        className="t-btn t-btn-ghost t-btn-block invitation-later"
        disabled={busy}
        onClick={() => navigate(nextPath, { replace: true })}
      >
        Not now
      </button>
    </AppShell>
  );
}
